import apiClient from '../lib/apiClient';

const employeeService = {
  // Get all employees with optional filters
  getEmployees: async (filters = {}) => {
    try {
      const result = await apiClient.get('/employees', filters);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch employees'
      };
    }
  },

  // Get employee by ID
  getEmployee: async (employeeId) => {
    try {
      const result = await apiClient.get(`/employees/${employeeId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch employee details' };
    }
  },

  // Create new employee
  createEmployee: async (employeeData) => {
    try {
      const result = await apiClient.post('/employees', employeeData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create employee' };
    }
  },

  // Update employee
  updateEmployee: async (employeeId, updateData) => {
    try {
      const result = await apiClient.put(`/employees/${employeeId}`, updateData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update employee' };
    }
  },

  // Delete employee
  deleteEmployee: async (employeeId) => {
    try {
      const result = await apiClient.delete(`/employees/${employeeId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete employee' };
    }
  },

  // Get employee statistics
  getEmployeeStats: async () => {
    try {
      const result = await apiClient.get('/employees/stats');
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch employee statistics' };
    }
  },

  // Bulk update employees
  bulkUpdateEmployees: async (employeeIds, updateData) => {
    try {
      const result = await apiClient.post('/employees/bulk-update', {
        ids: employeeIds,
        data: updateData
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to bulk update employees' };
    }
  },

  // Get employee documents
  getEmployeeDocuments: async (employeeId) => {
    const paths = [
      `/employees/${employeeId}/documents`,
      '/employee-self-service/documents',
      '/employees/self-documents',
    ];
    for (const path of paths) {
      try {
        const result = await apiClient.get(path);
        if (result.success) return result;
        const err = String(result.error || '').toLowerCase();
        if (!err.includes('404') && !err.includes('not found')) return result;
      } catch (error) {
        const err = String(error.message || '').toLowerCase();
        if (!err.includes('404') && !err.includes('not found')) {
          return { success: false, error: error.message || 'Failed to fetch documents' };
        }
      }
    }
    return { success: true, data: [] };
  },

  // Upload employee document
  uploadEmployeeDocument: async (employeeId, file, documentType) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType || 'Other');

      const token = localStorage.getItem('erp_auth_token');
      const baseURL = import.meta.env.VITE_API_BASE_URL || `http://${window.location.hostname}:8000/api`;
      const url = `${baseURL}/employees/${employeeId}/documents`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.data,
        error: data.error
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to upload document' };
    }
  },

  // Delete employee document
  deleteEmployeeDocument: async (employeeId, documentId) => {
    try {
      const result = await apiClient.delete(`/employees/${employeeId}/documents/${documentId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete document' };
    }
  },

  // Import employees from Excel
  importEmployees: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('erp_auth_token');
      const baseURL = import.meta.env.VITE_API_BASE_URL || `http://${window.location.hostname}:8000/api`;
      const url = `${baseURL}/employees/import-from-excel`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      return {
        success: data.success,
        data: data.data,
        error: data.error
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to import employees' };
    }
  },

  // Download CSV template
  downloadTemplate: async () => {
    try {
      const token = localStorage.getItem('erp_auth_token');
      const baseURL = import.meta.env.VITE_API_BASE_URL || `http://${window.location.hostname}:8000/api`;
      const url = `${baseURL}/employees/download-template`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download template');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'employee_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (error) {
      console.error('Download template error:', error);
      return { success: false, error: error.message };
    }
  }
};

export default employeeService;
